import React, { useState, useEffect } from 'react';
import MainSidebar from '../../components/ui/MainSidebar';

import DateRangeFilter from '../advanced-reporting/components/DateRangeFilter';
import MetricCard from '../workshop-dashboard/components/MetricCard';
import AnalyticsFilters from './components/AnalyticsFilters';
import PerformanceGrid from './components/PerformanceGrid';
import EngagementTrendsChart from './components/EngagementTrendsChart';
import PerformanceAlertsPanel from './components/PerformanceAlertsPanel';
import ExecutionPanel from './components/ExecutionPanel';
import { automationAnalyticsService } from '../../services/automationAnalyticsService';
import { trackEngagementMetric, trackPerformanceAlert, trackUserBehaviorFunnel } from '../../utils/analytics';

const AutomationAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState('last30days');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [filters, setFilters] = useState({
    triggerType: null,
    performanceRange: 'all'
  });
  const [dashboardData, setDashboardData] = useState({
    kpis: {},
    performanceMetrics: [],
    engagementTrends: [],
    alerts: []
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  useEffect(() => {
    // Initialize with last 30 days
    const today = new Date();
    const last30 = new Date(today);
    last30?.setDate(last30?.getDate() - 30);
    
    setDateRange({
      startDate: last30?.toISOString()?.split('T')?.[0],
      endDate: today?.toISOString()?.split('T')?.[0]
    });
  }, []);

  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      loadDashboardData();
    }
  }, [dateRange, filters?.triggerType]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const filterParams = {
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        triggerType: filters?.triggerType
      };

      const [kpis, performanceMetrics, engagementTrends, alerts] = await Promise.all([
        automationAnalyticsService?.getOverallKPIs(filterParams),
        automationAnalyticsService?.getPerformanceMetrics(filterParams),
        automationAnalyticsService?.getEngagementTrends(filterParams),
        automationAnalyticsService?.getPerformanceAlerts(filterParams)
      ]);

      // Apply performance range filter on client side
      let filteredMetrics = performanceMetrics;
      if (filters?.performanceRange !== 'all') {
        filteredMetrics = performanceMetrics?.filter(rule => {
          const openRate = parseFloat(rule?.openRate);
          switch (filters?.performanceRange) {
            case 'high':
              return openRate > 40;
            case 'medium':
              return openRate >= 20 && openRate <= 40;
            case 'low':
              return openRate < 20;
            default:
              return true;
          }
        });
      }

      setDashboardData({
        kpis,
        performanceMetrics: filteredMetrics,
        engagementTrends,
        alerts
      });

      // Track engagement metrics
      trackEngagementMetric('dashboard_kpis_loaded', {
        active_rules: kpis?.totalActiveRules,
        emails_sent: kpis?.totalEmailsSent,
        avg_open_rate: kpis?.avgOpenRate,
        avg_click_rate: kpis?.avgClickRate,
        avg_conversion_rate: kpis?.avgConversionRate,
        date_range: `${dateRange?.startDate}_to_${dateRange?.endDate}`
      });

      // Track performance alerts
      if (alerts?.length > 0) {
        alerts?.forEach(alert => {
          trackPerformanceAlert(alert?.type, {
            rule_id: alert?.ruleId,
            rule_name: alert?.ruleName,
            alert_message: alert?.message,
            severity: alert?.severity
          });
        });
      }

      // Track funnel step
      trackUserBehaviorFunnel('analytics_dashboard_viewed', {
        funnel_stage: 'view_campaign_performance',
        total_rules: filteredMetrics?.length,
        filter_trigger_type: filters?.triggerType || 'all',
        filter_performance_range: filters?.performanceRange
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setDashboardData({
        kpis: {},
        performanceMetrics: [],
        engagementTrends: [],
        alerts: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (newDateRange) => {
    setDateRange(prev => ({ ...prev, ...newDateRange }));
  };

  const handlePresetChange = (newPreset) => {
    setPreset(newPreset);
  };

  const handleFilterChange = (newFilters) => {
    trackUserBehaviorFunnel('analytics_filters_applied', {
      funnel_stage: 'filter_campaign_data',
      filters: newFilters
    });
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const kpiCards = [
    {
      icon: 'Zap',
      label: 'Reglas Activas',
      value: dashboardData?.kpis?.totalActiveRules || 0,
      trend: null,
      trendLabel: 'Total de reglas configuradas',
      color: 'primary'
    },
    {
      icon: 'Mail',
      label: 'Emails Enviados',
      value: dashboardData?.kpis?.totalEmailsSent?.toLocaleString() || '0',
      trend: null,
      trendLabel: 'Este período',
      color: 'accent'
    },
    {
      icon: 'Eye',
      label: 'Tasa de Apertura',
      value: `${dashboardData?.kpis?.avgOpenRate || 0}%`,
      trend: parseFloat(dashboardData?.kpis?.openRateTrend || 0),
      trendLabel: 'vs. período anterior',
      color: 'success'
    },
    {
      icon: 'MousePointer',
      label: 'Tasa de Clics',
      value: `${dashboardData?.kpis?.avgClickRate || 0}%`,
      trend: parseFloat(dashboardData?.kpis?.clickRateTrend || 0),
      trendLabel: 'vs. período anterior',
      color: 'primary'
    },
    {
      icon: 'Target',
      label: 'Tasa de Conversión',
      value: `${dashboardData?.kpis?.avgConversionRate || 0}%`,
      trend: parseFloat(dashboardData?.kpis?.conversionRateTrend || 0),
      trendLabel: 'vs. período anterior',
      color: 'warning'
    }
  ];

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar />
      
      <main className="flex-1 overflow-hidden ml-0 lg:ml-64">
        <div className="h-full overflow-y-auto">
          <div className="p-4 md:p-6 lg:p-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                  Análisis de Automatización
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Monitoreo de rendimiento y métricas de engagement de reglas de automatización
                </p>
              </div>
            </div>

            {/* Date Range Filter */}
            <DateRangeFilter
              startDate={dateRange?.startDate}
              endDate={dateRange?.endDate}
              onDateChange={handleDateChange}
              preset={preset}
              onPresetChange={handlePresetChange}
            />

            {/* Analytics Filters */}
            <AnalyticsFilters
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            {/* Real-time Execution Panel */}
            <ExecutionPanel />

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {kpiCards?.map((card, index) => (
                <MetricCard
                  key={index}
                  icon={card?.icon}
                  label={card?.label}
                  value={card?.value}
                  trend={card?.trend}
                  trendLabel={card?.trendLabel}
                  color={card?.color}
                />
              ))}
            </div>

            {/* Performance Alerts */}
            <PerformanceAlertsPanel
              alerts={dashboardData?.alerts}
              loading={loading}
            />

            {/* Engagement Trends Chart */}
            <EngagementTrendsChart
              data={dashboardData?.engagementTrends}
              loading={loading}
            />

            {/* Performance Grid */}
            <PerformanceGrid
              data={dashboardData?.performanceMetrics}
              loading={loading}
              onSort={handleSort}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AutomationAnalyticsDashboard;